import { Injectable, inject } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import {
  BaseHttpService,
  IListRequest,
  IListResponse,
  ListRequestSortOrderEnum,
} from 'O3_core';
import {
  ITicketCategory,
  ITicketSeverity,
  ITicketStatus,
} from '../models/ticket.interface';

/**
 * Every row, in the catalog's own order. `take: 100` is not paging — these
 * catalogs hold four to seven rows and a page is simply never reached.
 */
export const ALL_CATALOG_ROWS: IListRequest = {
  pagination: { skip: 0, take: 100 },
  sort: [{ field: 'sortOrder', order: ListRequestSortOrderEnum.Asc }],
};

/**
 * A small catalog fetched whole, every time.
 *
 * Split from the cached variant below rather than duplicated, because not
 * every catalog *can* be cached: the notification events are edited from the
 * settings screen, so a cache would serve the state before the edit.
 *
 * Named `all`, not `list`: `BaseHttpService.list(data)` already exists with a
 * paginated contract, and these are always fetched whole.
 */
export abstract class CatalogHttpService<T> extends BaseHttpService<T> {
  all(): Observable<T[]> {
    return this.post<IListResponse<T>>(
      `${this.apiUrl}/filter`,
      ALL_CATALOG_ROWS,
    ).pipe(map((response) => response.data.items));
  }
}

abstract class TicketCatalogHttpService<T> extends CatalogHttpService<T> {
  /**
   * Cached for the life of the application. These are four to seven rows that
   * change when someone edits a catalog, not per user and not per request —
   * refetching them for every grid render and every dropdown open would be
   * pure noise on the wire.
   */
  private cached?: Observable<T[]>;

  override all(): Observable<T[]> {
    this.cached ??= super
      .all()
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    return this.cached;
  }
}

@Injectable({ providedIn: 'root' })
export class TicketStatusService extends TicketCatalogHttpService<ITicketStatus> {
  constructor() {
    super({ service: 'support', feature: 'ticket-status', version: 'v1' });
  }
}

@Injectable({ providedIn: 'root' })
export class TicketSeverityService extends TicketCatalogHttpService<ITicketSeverity> {
  constructor() {
    super({ service: 'support', feature: 'ticket-severity', version: 'v1' });
  }
}

@Injectable({ providedIn: 'root' })
export class TicketCategoryService extends TicketCatalogHttpService<ITicketCategory> {
  constructor() {
    super({ service: 'support', feature: 'ticket-category', version: 'v1' });
  }
}

/** Injects all three, for screens that need more than one. */
export function injectTicketCatalogs() {
  return {
    statuses: inject(TicketStatusService),
    severities: inject(TicketSeverityService),
    categories: inject(TicketCategoryService),
  };
}
